import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import type { PhotoSlot } from "@shyne/shared/api";
import { AppointmentWorkflow } from "@/models/appointment-task.model";
import { AppointmentPhotoRepository } from "@/repositories/appointment-photos.repository";
import { AppointmentChecklistRepository } from "@/repositories/appointment-checklist.repository";
import { EMPTY_CHECKLIST, newerChecklist, type ChecklistState } from "@/lib/appointment-checklist";
import { getChecklistProgress, getChecklistGateError } from "@/lib/utils/checklist-progress";

export function useAppointmentChecklist(appointmentId: string, serviceId: string | null | undefined, ready: boolean) {
  const [checklist, setChecklist] = useState<ChecklistState>(EMPTY_CHECKLIST);
  const [serviceWorkflows, setServiceWorkflows] = useState<PhotoSlot[]>([]);
  const [appointmentWorkflows, setAppointmentWorkflows] = useState<AppointmentWorkflow[]>([]);
  const [checklistLoaded, setChecklistLoaded] = useState(false);
  const [workflowsLoaded, setWorkflowsLoaded] = useState(false);
  const [checklistError, setChecklistError] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  const retryChecklist = useCallback(() => setReloadKey((k) => k + 1), []);

  const showTickAnswer = useCallback((answered: ChecklistState) => setChecklist((shown) => newerChecklist(shown, answered)), []);

  useEffect(() => {
    if (!ready) return;
    let cancelled = false;
    setChecklistLoaded(false);
    setWorkflowsLoaded(false);
    setChecklistError(false);

    AppointmentChecklistRepository.read(appointmentId)
      .then((loaded) => {
        if (cancelled) return;
        setChecklist(loaded);
        setChecklistLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load task checklist:", err);
        if (!cancelled) {
          setChecklistError(true);
          toast.error("Failed to load the task checklist. Tap retry to try again.");
        }
      });

    Promise.all([
      serviceId ? AppointmentPhotoRepository.slots(appointmentId) : Promise.resolve([] as PhotoSlot[]),
      AppointmentPhotoRepository.read(appointmentId),
    ])
      .then(([serviceWorkflowData, appointmentWorkflowData]) => {
        if (cancelled) return;
        setServiceWorkflows(serviceWorkflowData);
        setAppointmentWorkflows(appointmentWorkflowData);
        setWorkflowsLoaded(true);
      })
      .catch((err) => {
        console.error("Failed to load photo workflows:", err);
        if (!cancelled) {
          setChecklistError(true);
          toast.error("Failed to load photo workflows. Tap retry to try again.");
        }
      });

    return () => {
      cancelled = true;
    };
  }, [appointmentId, serviceId, ready, reloadKey]);

  const { serviceTasks, appointmentTasks } = checklist;

  const checklistProgress = useMemo(() => getChecklistProgress(serviceTasks, appointmentTasks), [serviceTasks, appointmentTasks]);

  const checklistGateError = useMemo(
    () => getChecklistGateError(checklistProgress, checklistLoaded),
    [checklistProgress, checklistLoaded]
  );

  return {
    serviceTasks,
    serviceWorkflows,
    appointmentTasks,
    showTickAnswer,
    appointmentWorkflows,
    setAppointmentWorkflows,
    checklistLoaded,
    workflowsLoaded,
    checklistError,
    retryChecklist,
    checklistProgress,
    checklistGateError,
  };
}
